import { PaginationQuery, SearchItem, SortColDef } from "@/types";
import { useCallback, useMemo } from "react";
import {
  ArrayParam,
  NumberParam,
  QueryParamConfigMap,
  UrlUpdateType,
  useQueryParams,
  withDefault,
} from "use-query-params";

interface PageQueryConfig {
  includePagination?: boolean;
  defaultQuery?: QueryParamConfigMap;
  defaultPagination?: Partial<PaginationQuery>;
}

export const DefaultPageSizes = [10, 20, 50];

export function queryParamsFromSearchItems(
  items: SearchItem[],
  config?: PageQueryConfig,
) {
  type Type = {
    [K in (typeof items)[number]["key"]]: Extract<
      (typeof items)[number],
      { key: K }
    >["pageQueryConfig"];
  };
  const temp = items
    .filter((one) => one.pageQueryConfig)
    .map((one) => [one.key, one.pageQueryConfig]);
  if (config?.includePagination) {
    temp.push([
      "size",
      withDefault(
        NumberParam,
        config?.defaultPagination?.size ?? DefaultPageSizes[0],
      ),
    ]);
    temp.push([
      "page",
      withDefault(NumberParam, config?.defaultPagination?.page ?? 1),
    ]);
    temp.push([
      "sorts",
      withDefault(ArrayParam, config?.defaultPagination?.sorts ?? []),
    ]);
    temp.push([
      "dirs",
      withDefault(ArrayParam, config?.defaultPagination?.dirs ?? []),
    ]);
  }
  return Object.fromEntries(temp) as Type;
}

export function usePageQuery<T>(
  items: SearchItem[],
  config?: PageQueryConfig,
): [
  T,
  (key: string, val: unknown) => void,
  (params: Partial<T> | ((old: T) => T)) => void,
  (params: Partial<T>, type?: UrlUpdateType) => void,
] {
  // type SearchProps = {
  //   [K in typeof items[number]['key']]:
  //   Exclude<Extract<typeof items[number], { key: K }>["pageQueryConfig"], undefined>;
  // };
  const queryConfig = useMemo(() => {
    let PaginationQueryConfig = {};
    if (config?.includePagination) {
      PaginationQueryConfig = {
        size: withDefault(
          NumberParam,
          config?.defaultPagination?.size ?? DefaultPageSizes[0],
        ),
        page: withDefault(NumberParam, config?.defaultPagination?.page ?? 1),
        sorts: withDefault(ArrayParam, config?.defaultPagination?.sorts ?? []),
        dirs: withDefault(ArrayParam, config?.defaultPagination?.dirs ?? []),
      };
    }
    return {
      ...config?.defaultQuery,
      ...queryParamsFromSearchItems(items as SearchItem[]),
      ...PaginationQueryConfig,
    } as const;
  }, [
    items,
    config?.defaultQuery,
    config?.defaultPagination,
    config?.includePagination,
  ]);

  const [query, setQuery] = useQueryParams(queryConfig);
  const changeParam = useCallback(
    (key: string, value: unknown) => {
      setQuery((old) => {
        return {
          ...old,
          [key]: value,
        };
      });
    },
    [setQuery],
  );

  const changeParams = useCallback(
    (params: Partial<T> | ((old: T) => T)) => {
      setQuery((old) => {
        if (typeof params === "function") {
          return params(old as T);
        } else {
          return {
            ...old,
            ...params,
          };
        }
      });
    },
    [setQuery],
  );

  return [query as T, changeParam, changeParams, setQuery];
}

export function getDefaultSorts(
  cols: SortColDef<any>[],
): Pick<PaginationQuery, "sorts" | "dirs"> {
  const sorts: PaginationQuery["sorts"] = [];
  const dirs: PaginationQuery["dirs"] = [];
  cols.forEach((one) => {
    if (one.initialSort && one.sortKey) {
      sorts.push(one.sortKey);
      dirs.push(one.initialSort);
    }
  });
  return { sorts, dirs };
}
